/**
 * Legal Disclaimer Component
 * Displays important legal notices for gambling/betting strategy content
 */

import { AlertTriangle, Info } from 'lucide-react';

interface LegalDisclaimerProps {
  variant?: 'prominent' | 'compact';
  className?: string;
}

export default function LegalDisclaimer({
  variant = 'compact',
  className = '',
}: LegalDisclaimerProps): JSX.Element {
  if (variant === 'prominent') {
    return (
      <div
        className={`p-6 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-800 rounded-xl ${className}`}
        role="alert"
        aria-live="polite"
      >
        <div className="flex items-start gap-4">
          <div className="p-2 bg-amber-100 dark:bg-amber-900/40 rounded-lg flex-shrink-0">
            <AlertTriangle size={24} className="text-amber-700 dark:text-amber-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-amber-900 dark:text-amber-200 mb-2">
              Important Legal Notice
            </h3>
            <div className="space-y-2 text-sm text-amber-800 dark:text-amber-300">
              <p>
                <strong>Educational Content Only:</strong> All products sold on this platform are
                informational and educational materials about gambling and betting strategies.
              </p>
              <p>
                <strong>No Guaranteed Outcomes:</strong> We do not guarantee winnings, profits, or
                any specific outcomes. Past performance and examples do not guarantee future results.
              </p>
              <p>
                <strong>Not Financial Advice:</strong> This content is not financial, legal, or
                professional advice. You are solely responsible for your own decisions and actions.
              </p>
              <p>
                <strong>Age & Legal Compliance:</strong> You must be 18+ years old and comply with
                all local laws regarding gambling-related content in your jurisdiction.
              </p>
              <p>
                <strong>No Gambling Services:</strong> Foxie Systems does not operate gambling
                services, accept bets, or facilitate any wagering activities. We are a content
                marketplace only.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Compact variant
  return (
    <div
      className={`p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg ${className}`}
      role="note"
    >
      <p className="text-xs text-gray-600 dark:text-gray-400 flex items-start gap-2">
        <Info size={14} className="flex-shrink-0 mt-0.5 text-gray-500 dark:text-gray-500" />
        <span>
          <strong>Disclaimer:</strong> All products are for educational and entertainment purposes
          only. We do not guarantee winnings or outcomes. This is not financial advice. You must be
          18+ and comply with your local laws regarding gambling-related content. Foxie Systems does
          not operate gambling services or facilitate betting.
        </span>
      </p>
    </div>
  );
}
